import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, XMarkIcon, PlusIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { environmentAPI } from '../services/api';
import toast from 'react-hot-toast';

const EnvironmentSelector = ({ 
  selectedEnvironments = [], 
  onChange, 
  maxSelections = null,
  allowPrimary = true,
  className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const dropdownRef = useRef(null);

  // Fetch environments
  const { data: environmentsResponse, isLoading, error } = useQuery({
    queryKey: ['environments', searchTerm],
    queryFn: () => environmentAPI.getAll({ 
      search: searchTerm || undefined,
      limit: 50 
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once on failure
    onError: (error) => {
      console.error('Failed to fetch environments:', error);
      // Don't show toast here to avoid spam, just log it
    }
  });

  const environments = environmentsResponse?.data?.data?.environments || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEnvironmentSelect = (environment) => {
    // Check if already selected
    const isAlreadySelected = selectedEnvironments.some(
      se => se.environmentId === environment.id
    );
    
    if (isAlreadySelected) {
      toast.error('Environment already selected');
      return;
    }

    // Check max selections
    if (maxSelections && selectedEnvironments.length >= maxSelections) {
      toast.error(`You can only select up to ${maxSelections} environments`);
      return;
    }

    const newSelection = {
      environmentId: environment.id,
      isPrimary: selectedEnvironments.length === 0 && allowPrimary,
      notes: ''
    };
    
    onChange([...selectedEnvironments, newSelection]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleRemoveEnvironment = (environmentId) => {
    const updated = selectedEnvironments.filter(se => se.environmentId !== environmentId);
    
    // If we removed the primary, make the first one primary
    if (allowPrimary && updated.length > 0) {
      const hadPrimary = selectedEnvironments.some(se => se.environmentId === environmentId && se.isPrimary);
      if (hadPrimary) {
        updated[0].isPrimary = true;
      }
    }
    
    onChange(updated);
  };

  const handleTogglePrimary = (environmentId) => {
    if (!allowPrimary) return;
    
    const updated = selectedEnvironments.map(se => ({
      ...se,
      isPrimary: se.environmentId === environmentId
    }));
    onChange(updated);
  };

  const handleNotesChange = (environmentId, notes) => {
    const updated = selectedEnvironments.map(se => 
      se.environmentId === environmentId ? { ...se, notes } : se
    );
    onChange(updated);
  };


  const getEnvironmentById = (id) => {
    return environments.find(env => env.id === id);
  };

  return (
    <div className={className} ref={dropdownRef}>
      {/* Selected Environments */}
      {selectedEnvironments.length > 0 && (
        <div className="mb-3 space-y-2">
          {selectedEnvironments.map((selectedEnv) => {
            const environment = getEnvironmentById(selectedEnv.environmentId);
            if (!environment) return null;
            
            return (
              <div 
                key={selectedEnv.environmentId}
                className="bg-gray-50 dark:bg-gray-600/20 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {environment.name}
                    </span>
                    {allowPrimary && (
                      <button
                        type="button"
                        onClick={() => handleTogglePrimary(selectedEnv.environmentId)}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          selectedEnv.isPrimary
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        {selectedEnv.isPrimary ? 'Primary' : 'Make Primary'}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveEnvironment(selectedEnv.environmentId)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                
                {environment.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {environment.description}
                  </p>
                )}
                
                <textarea
                  placeholder="Notes (optional)"
                  value={selectedEnv.notes || ''}
                  onChange={(e) => handleNotesChange(selectedEnv.environmentId, e.target.value)}
                  rows={2}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Add Environment Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={maxSelections && selectedEnvironments.length >= maxSelections}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Add Environment
        </span>
        <ChevronDownIcon className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          {/* Search */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-600">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search environments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 text-sm"
              />
            </div>
          </div>

          {/* Environment List */}
          <div className="max-h-auto overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Loading environments...
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-500 dark:text-red-400">
                <p className="text-sm">Failed to load environments</p>
                <p className="text-xs mt-1">You can still add custom environments below</p>
              </div>
            ) : environments.length > 0 ? (
              environments.map((environment) => {
                const isSelected = selectedEnvironments.some(se => se.environmentId === environment.id);
                
                return (
                  <button
                    type="button"
                    key={environment.id}
                    onClick={() => handleEnvironmentSelect(environment)}
                    disabled={isSelected}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${
                      isSelected ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {environment.name}
                          </span>
                          {/* {environment.isPredefined && (
                            <span className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                              Built-in
                            </span>
                          )} */}
                          {/* {environment.category && (
                            <span className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                              {environment.category}
                            </span>
                          )} */}
                        </div>
                        {environment.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {environment.description}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <span className="text-green-600 dark:text-green-400 text-sm">Selected</span>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No environments found
              </div>
            )}
          </div>

          {/* Create Custom Environment */}
          {/* <div className="border-t border-gray-200 dark:border-gray-600">
            {!showCustomForm ? (
              <button
                type="button"
                onClick={() => setShowCustomForm(true)}
                className="w-full px-4 py-3 text-left text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <PlusIcon className="w-4 h-4" />
                  Create custom environment
                </div>
              </button>
            ) : (
              <div className="p-4 space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-white">Create Custom Environment</h4>
                
                <div>
                  <input
                    type="text"
                    placeholder="Environment name *"
                    value={customEnvironment.name}
                    onChange={(e) => setCustomEnvironment(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    placeholder="Category (optional)"
                    value={customEnvironment.category}
                    onChange={(e) => setCustomEnvironment(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  />
                </div>
                
                <div>
                  <textarea
                    placeholder="Description (optional)"
                    value={customEnvironment.description}
                    onChange={(e) => setCustomEnvironment(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  />
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCreateCustom}
                    disabled={createEnvironmentMutation.isLoading || !customEnvironment.name.trim()}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createEnvironmentMutation.isLoading ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomForm(false);
                      setCustomEnvironment({ name: '', description: '', category: '', extra_data: {} });
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div> */}
        </div>
      )}
    </div>
  );
};

export default EnvironmentSelector;
